import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useWeb3React } from '@web3-react/core';
import './Vaults.css';
import back3Image from '../../assets/back3.png';
import WalletModal from '../WalletModal/WalletModal';

interface VaultResolver {
  id: string;
  name: string;
  icon: string;
  iconType?: 'emoji' | 'image';
  iconBg: string;
  status: string;
  showVerifiedBadge?: boolean;
  networkShare: string;
  maxAPR: string;
  endsOn: string | React.ReactNode;
  dailyRewards: string;
  rewardToken: string;
  isContest?: boolean;
}

const Vaults: React.FC = () => {
  const { account, isActive } = useWeb3React();
  const [selectedTab, setSelectedTab] = useState<'stake' | 'unstake'>('stake');
  const [stakeAmount, setStakeAmount] = useState('');
  const [selectedPercentage, setSelectedPercentage] = useState<number | null>(null);
  const [selectedToken, setSelectedToken] = useState('USDP');
  const [selectedLockTime, setSelectedLockTime] = useState<30 | 60 | 90>(30);
  const [isWalletModalOpen, setIsWalletModalOpen] = useState(false);
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  useEffect(() => {
    const calculateTime = () => {
      const targetDate = new Date('2025-12-20T00:00:00').getTime();
      const now = new Date().getTime();
      const remaining = targetDate - now;

      if (remaining <= 0) {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        return;
      }

      const days = Math.floor(remaining / (1000 * 60 * 60 * 24));
      const hours = Math.floor((remaining % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((remaining % (1000 * 60)) / 1000);

      setTimeLeft({ days, hours, minutes, seconds });
    };

    calculateTime();
    const timer = setInterval(calculateTime, 1000);
    return () => clearInterval(timer);
  }, []);

  const formatCountdown = () => {
    return (
      <div className="vault-countdown">
        <span>{timeLeft.days}d</span> :
        <span>{String(timeLeft.hours).padStart(2, '0')}h</span> :
        <span>{String(timeLeft.minutes).padStart(2, '0')}m</span> :
        <span>{String(timeLeft.seconds).padStart(2, '0')}s</span>
      </div>
    );
  };

  const vaultResolvers: VaultResolver[] = [
    {
      id: 'usd-protocol',
      name: 'E-Pay Dollar USD Vault Protocol',
      icon: '/USDPlogo.png',
      iconType: 'image',
      iconBg: 'linear-gradient(135deg, #059669 0%, #10b981 50%, #34d399 100%)',
      status: 'Verified',
      networkShare: '75.5%',
      maxAPR: '14.8%',
      endsOn: '31 Dec 2026',
      dailyRewards: '12,458.71',
      rewardToken: 'USDP'
    },
    {
      id: 'tether-vault',
      name: 'Tether Treasury Vault Protocol',
      icon: '/icons/USDT.svg',
      iconType: 'image',
      iconBg: 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 50%, #fcd34d 100%)',
      status: 'Verified',
      networkShare: '72.1%',
      maxAPR: '4.5%',
      endsOn: '28 Feb 2026',
      dailyRewards: '11,284.89',
      rewardToken: 'USDT'
    },
    {
      id: 'usdc-vault',
      name: 'USDC Treasury Vault Protocol',
      icon: '/icons/USDC.svg',
      iconType: 'image',
      iconBg: 'linear-gradient(135deg, #007BFF, #0056B3)',
      status: 'Verified',
      networkShare: '62%',
      maxAPR: '12.3%',
      endsOn: '31 Dec 2026',
      dailyRewards: '10,027.42',
      rewardToken: 'USDC'
    },
    // Prize Contest Vaults
    {
      id: 'prize-vault-1',
      name: 'Prize Contest Vault 1',
      icon: '/USDPlogo.png',
      iconType: 'image',
      iconBg: 'linear-gradient(135deg, #FFD700 0%, #FDB931 50%, #FDB931 100%)',
      status: 'Contest',
      networkShare: '50M Cap',
      maxAPR: '25%',
      endsOn: formatCountdown(),
      dailyRewards: 'High Yield',
      rewardToken: 'USDP',
      isContest: true
    },
    {
      id: 'prize-vault-2',
      name: 'Prize Contest Vault 2',
      icon: '/USDPlogo.png',
      iconType: 'image',
      iconBg: 'linear-gradient(135deg, #C0C0C0 0%, #E0E0E0 50%, #C0C0C0 100%)',
      status: 'Locked',
      networkShare: '100M Cap',
      maxAPR: '20%',
      endsOn: 'Until Filled',
      dailyRewards: 'High Yield',
      rewardToken: 'USDP',
      isContest: true
    },
    {
      id: 'prize-vault-3',
      name: 'Prize Contest Vault 3',
      icon: '/USDPlogo.png',
      iconType: 'image',
      iconBg: 'linear-gradient(135deg, #CD7F32 0%, #D2691E 50%, #CD7F32 100%)',
      status: 'Locked',
      networkShare: '150M Cap',
      maxAPR: '15%',
      endsOn: 'Until Filled',
      dailyRewards: 'High Yield',
      rewardToken: 'USDP',
      isContest: true
    },
    {
      id: 'prize-vault-4',
      name: 'Prize Contest Vault 4',
      icon: '/USDPlogo.png',
      iconType: 'image',
      iconBg: 'linear-gradient(135deg, #4B0082 0%, #8A2BE2 50%, #4B0082 100%)',
      status: 'Locked',
      networkShare: '200M Cap',
      maxAPR: '7.5%',
      endsOn: 'Until Filled',
      dailyRewards: 'Standard',
      rewardToken: 'USDP',
      isContest: true
    },
    {
      id: 'prize-vault-5',
      name: 'Prize Contest Vault 5',
      icon: '/USDPlogo.png',
      iconType: 'image',
      iconBg: 'linear-gradient(135deg, #000080 0%, #0000FF 50%, #000080 100%)',
      status: 'Locked',
      networkShare: '500M Cap',
      maxAPR: '6%',
      endsOn: 'Until Filled',
      dailyRewards: 'Standard',
      rewardToken: 'USDP',
      isContest: true
    }
  ];

  const handlePercentageClick = (percentage: number) => {
    setSelectedPercentage(percentage);
    const mockBalance = 1000;
    const amount = (mockBalance * percentage / 100).toString();
    setStakeAmount(amount);
  };

  const handleConnectWallet = () => {
    if (!isActive) {
      setIsWalletModalOpen(true);
    }
  };

  const formatAddress = (address: string) => {
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  const calculateLockedTillDate = (days: number) => {
    const futureDate = new Date();
    futureDate.setDate(futureDate.getDate() + days);
    return futureDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const getRewardTokenForDisplay = () => {
    switch (selectedToken) {
      case 'USDP':
        return 'USDP won';
      case 'USDT':
        return 'USDT won';
      case 'USDC':
        return 'USDC won';
      default:
        return 'USDP won';
    }
  };

  return (
    <section id="vaults" className="vaults section-padding">
      <div className="container">
        <div className="vaults-content">
          {/* Vault Resolvers Grid */}
          <div className="vault-resolvers-grid">
            {vaultResolvers.map((resolver, index) => (
              <motion.div
                key={resolver.id}
                className={`vault-resolver-card ${resolver.status === 'Locked' ? 'locked' : ''}`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={resolver.status !== 'Locked' ? { scale: 1.02 } : {}}
              >
                <div className="resolver-header">
                  <div className="resolver-icon" style={{ background: resolver.iconBg }}>
                    {resolver.iconType === 'image' ? (
                      <img
                        src={resolver.icon}
                        alt={resolver.name}
                        className={`resolver-image ${resolver.icon.includes('USDT 1.png') ? 'usdt-image' : ''}`}
                      />
                    ) : (
                      <span className="resolver-emoji">{resolver.icon}</span>
                    )}
                    {resolver.showVerifiedBadge !== false && (
                      <div className="verified-badge">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                          <circle cx="12" cy="12" r="10" fill="#10b981"/>
                          <path d="M9 12L11 14L15 10" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </div>
                    )}
                  </div>
                </div>

                <h3 className="resolver-name">{resolver.name}</h3>
                <div className="resolver-status">{resolver.status}</div>

                <div className="resolver-metrics">
                  <div className="metric-row">
                    <span className="metric-label">Network share</span>
                    <span className="metric-value blue-large">{resolver.networkShare}</span>
                  </div>
                  
                  <div className="metric-row">
                    <span className="metric-label">Max APR</span>
                    <span className="metric-value blue-large">{resolver.maxAPR}</span>
                  </div>
                  
                  <div className="metric-row">
                    <span className="metric-label">{resolver.isContest ? 'Time Remaining' : 'Ends on'}</span>
                    <span className="metric-value blue-medium">{resolver.endsOn}</span>
                  </div>
                  
                  <div className="metric-row">
                    <span className="metric-label">Daily rewards</span>
                    <div className="rewards-display">
                      <span className="reward-amount">{resolver.dailyRewards}</span>
                      <span className="reward-token">{resolver.rewardToken}</span>
                    </div>
                  </div>
                </div>

                <motion.button
                  className="delegate-button"
                  whileHover={resolver.status !== 'Locked' ? { scale: 1.02 } : {}}
                  whileTap={resolver.status !== 'Locked' ? { scale: 0.98 } : {}}
                  disabled={resolver.status === 'Locked'}
                >
                  {resolver.status === 'Locked' ? 'Locked' : 'Delegate'}
                </motion.button>
              </motion.div>
            ))}
          </div>

          {/* Staking Section */}
          <motion.div
            className="staking-section"
            style={{ '--bg-image': `url(${back3Image})` } as any}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            viewport={{ once: true }}
          >
            <div className="staking-left">
              <div className="staking-steps">
                <div className="step-item">
                  <div className="step-number">1</div>
                  <div className="step-content">
                    <h3 className="step-title">Stake</h3>
                    <p className="step-description">Stake your stablecoin tokens to earn yield and rewards</p>
                  </div>
                </div>

                <div className="step-item">
                  <div className="step-number">2</div>
                  <div className="step-content">
                    <h3 className="step-title">Delegate</h3>
                    <p className="step-description">Delegate your staked tokens to vault resolvers and earn additional rewards</p>
                  </div>
                </div>
              </div>

              <div className="governance-section">
                <h3 className="governance-title">Protocol Governance</h3>
                <p className="governance-subtitle">
                  Participate in protocol improvements and decision making
                </p>
                <p className="governance-description">
                  Independently from delegating to resolvers, you can participate in DAO activities. Create
                  proposals, vote for them or delegate your voting power to a trusted delegate on
                  our <button className="governance-link" style={{background: 'none', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit', color: 'inherit'}}>Snapshot</button> and <button className="governance-link" style={{background: 'none', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit', color: 'inherit'}}>participate in the governance forum</button>
                </p>
              </div>
            </div>

            <div className="staking-right">
              <div className="staking-widget">
                <div className="staking-tabs">
                  <button
                    className={`tab-button ${selectedTab === 'stake' ? 'active' : ''}`}
                    onClick={() => setSelectedTab('stake')}
                  >
                    Stake
                  </button>
                  <button
                    className={`tab-button ${selectedTab === 'unstake' ? 'active' : ''}`}
                    onClick={() => setSelectedTab('unstake')}
                  >
                    Unstake
                  </button>
                </div>

                <div className="amount-section">
                  <div className="amount-header">
                    <span className="amount-label">Amount</span>
                    <span className="balance-info">
                      Balance: 0
                      <span className="max-button">MAX</span>
                      {isActive && (
                        <span className="wallet-status connected">
                          {formatAddress(account!)}
                        </span>
                      )}
                    </span>
                  </div>
                  
                  <div className="amount-input-container">
                    <div className="token-selector">
                      <img
                        src={
                          selectedToken === 'USDT' ? '/icons/USDT.svg' :
                          selectedToken === 'USDP' ? '/USDPlogo.png' :
                          selectedToken === 'USDC' ? '/icons/USDC.svg' :
                          '/USDPlogo.png'
                        }
                        alt={selectedToken}
                        className="token-icon"
                      />
                      <select
                        className="token-dropdown"
                        value={selectedToken}
                        onChange={(e) => setSelectedToken(e.target.value)}
                      >
                        <option value="USDP">USDP</option>
                        <option value="USDT">USDT</option>
                        <option value="USDC">USDC</option>
                      </select>
                    </div>
                    <input
                      type="text"
                      className="amount-input"
                      placeholder="0"
                      value={stakeAmount}
                      onChange={(e) => setStakeAmount(e.target.value)}
                    />
                  </div>

                  <div className="token-value">{selectedToken} Token ~$0.00</div>
                </div>

                <div className="percentage-selector">
                  {[25, 50, 75, 100].map((percentage) => (
                    <button
                      key={percentage}
                      className={`percentage-button ${selectedPercentage === percentage ? 'active' : ''}`}
                      onClick={() => handlePercentageClick(percentage)}
                    >
                      {percentage}%
                    </button>
                  ))}
                </div>

                <div className="reward-info">
                  <div className="info-row">
                    <span className="info-label">{getRewardTokenForDisplay()}</span>
                    <span className="info-value">0</span>
                  </div>
                </div>

                <div className="lock-details">
                  <div className="detail-row">
                    <span className="detail-label">Lock time</span>
                    <div className="locktime-selector">
                      {([30, 60, 90] as const).map((days) => (
                        <button
                          key={days}
                          className={`locktime-button ${selectedLockTime === days ? 'active' : ''}`}
                          onClick={() => setSelectedLockTime(days)}
                        >
                          {days} days
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Stake</span>
                    <span className="detail-value">0 {selectedToken}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Locked till</span>
                    <span className="detail-value">{calculateLockedTillDate(selectedLockTime)}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Network Fee</span>
                    <span className="detail-value fee">Market ~$0.13</span>
                  </div>
                </div>

                {!isActive ? (
                  <motion.button
                    className="stake-action-button connect-wallet"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleConnectWallet}
                  >
                    Connect Wallet
                  </motion.button>
                ) : (
                  <motion.button
                    className="stake-action-button"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    disabled={!stakeAmount}
                  >
                    Stake
                  </motion.button>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Wallet Modal */}
      <WalletModal
        isOpen={isWalletModalOpen}
        onClose={() => setIsWalletModalOpen(false)}
      />
    </section>
  );
};

export default Vaults;